import React, { useState, useEffect, useRef } from 'react';
import { Alert, Form, Button, Card, FilterGrid, FilterField } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import { ServiceArea } from '../../../services/areasService';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { projectService } from '../../../services/projectService';
import { Project, ProjectType, ProjectStatus } from '../../../types/Project';
import ProjectSummaryTotalsCard from './ProjectSummaryTotalsCard';
import { formatDollars } from '../../../utils/formatDollars';
import './ProjectSummaryTotals.css';

interface QBSProjectSummaryViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

interface ProjectSummaryRow {
  id: number;
  projectId: number;
  projectNumber: string | null;
  sageJobNumber: string | null;
  projectName: string;
  budget: number;
  costsUsed: number;
  costsInAction: number;
  poCosts: number;
  poRemaining: number;
  estRemainingBudget: number;
  estTotalCosts: number;
  value: number;
  estGrossMargin: number;
  estGrossMarginPercent: number;
  invoiced: number;
  invoiceRemaining: number;
}

type ProjectSummaryTotals = Record<string, number>;

const EMPTY_TOTALS: ProjectSummaryTotals = {
  totalBudget: 0,
  totalCostsUsed: 0,
  totalPoRemaining: 0,
  totalEstTotalCosts: 0,
  totalValue: 0,
  totalEstGrossMargin: 0,
  totalEstGrossMarginPercent: 0
};

/** Column definitions agreed with operations (Gord Miller, Oct 2026); shown as header tooltips. */
const COLUMN_HELP: Record<string, string> = {
  budget: 'From the project set-up.',
  costsUsed: 'Approved and pending submissions: employee time and hired equipment.',
  costsInAction: 'Submitted costs on tasks that are still planned or active.',
  poCosts: 'Approved purchase-order lines on the project\'s tasks.',
  poRemaining: 'PO costs not yet covered by submissions against those POs.',
  estRemainingBudget: 'Budget − Costs used − PO remaining.',
  estTotalCosts: 'Costs used + PO remaining.',
  value: 'Project value (efforts and other costs at project markups, plus marked-up PO remaining). Moves to a set-up field once one exists.',
  estGrossMargin: 'Value − Est. total costs.',
  estGrossMarginPercent: 'Est. gross margin ÷ Value.',
  invoiced: 'Finalized invoices.',
  invoiceRemaining: 'Invoices still pending or new.'
};

const QBSProjectSummaryView: React.FC<QBSProjectSummaryViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const currentYear = new Date().getFullYear().toString();

  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    fiscalYear: [currentYear] as string[],
    projectTypes: [] as string[],
    projectStatuses: [] as string[],
    projectIds: [] as string[]
  });
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    let cancelled = false;
    projectService
      .getAllProjects()
      .then((projects) => {
        if (!cancelled) setAllProjects(projects);
      })
      .catch(() => {
        if (!cancelled) setAllProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projectOptions = allProjects
    .filter((project) => !filters.serviceArea || String(project.serviceAreaId) === filters.serviceArea)
    .sort((a, b) => (a.projectNumber || a.name).localeCompare(b.projectNumber || b.name, undefined, { numeric: true }));

  const summaryFilters = () => ({
    fiscalYears: filters.fiscalYear,
    projectTypes: filters.projectTypes as ProjectType[],
    projectStatuses: filters.projectStatuses as ProjectStatus[],
    serviceAreaId: filters.serviceArea ? parseInt(filters.serviceArea) : undefined,
    projectIds: filters.projectIds.map(Number)
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [projectSummaryData, setProjectSummaryData] = useState<ProjectSummaryRow[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {}
  });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; projectId: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [summaryTotals, setSummaryTotals] = useState<ProjectSummaryTotals>(EMPTY_TOTALS);

  // Fiscal year options (current year plus adjacent years)
  const fiscalYearOptions = [
    (Number(currentYear) - 1).toString(),
    currentYear,
    (Number(currentYear) + 1).toString()
  ];

  // Project type options
  const projectTypeOptions: ProjectType[] = ['H200', 'Internal', 'Other Revenue', 'Routine', 'Quantified', 'CMC/MVI'];

  // Project status options (mapped to Closed and Active as per requirements)
  const projectStatusOptions: { value: ProjectStatus; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Closed' }
  ];

  // Update service area filter when defaultServiceAreaId changes
  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId, filters.serviceArea]);

  const handlePageChange = async (
    page: number,
    pageSize?: number,
    // Passed explicitly by handleSort: the sort state set there hasn't landed yet when this runs.
    sort: { sortField: string; sortDirection: 'asc' | 'desc' } = { sortField, sortDirection }
  ) => {
    const limit = pageSize || pagination.limit;
    setLoadingPreview(true);
    try {
      const response = await projectService.getProjectSummary(summaryFilters(), {
        page,
        limit
      }, sort);
      setProjectSummaryData(response.data);
      setPagination(prev => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
      // Update totals from backend (calculated from all filtered projects)
      if (response.totals) {
        setSummaryTotals(response.totals);
      }
    } catch (error) {
      console.error('Error loading page:', error);
      setErrorMessage('Failed to load page. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    handlePageChange(1, newPageSize);
  };

  // Sorting runs on the server across every project, then the page is cut.
  const handleSort = (field: string) => {
    const direction = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
    handlePageChange(1, undefined, { sortField: field, sortDirection: direction });
  };

  const handleGeneratePreview = async () => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      // Fetch first page for preview (50 results)
      const response = await projectService.getProjectSummary(summaryFilters(), {
        page: 1,
        limit: 50
      }, { sortField, sortDirection });
      setProjectSummaryData(response.data);
      setPagination({
        page: 1,
        limit: 50,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        onPageChange: () => {}
      });
      // Update totals from backend (calculated from all filtered projects)
      if (response.totals) {
        setSummaryTotals(response.totals);
      } else {
        // Reset totals if not provided
        setSummaryTotals(EMPTY_TOTALS);
      }
    } catch (error) {
      console.error('Error generating project summary:', error);
      setErrorMessage('Failed to generate QBS Project Summary report. Please try again later.');
      setProjectSummaryData([]);
      setPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        onPageChange: () => {}
      });
      // Reset totals on error
      setSummaryTotals(EMPTY_TOTALS);
    } finally {
      setLoadingPreview(false);
    }
  };

  const formatCurrency = (amount: number) =>
    formatDollars(Math.round(amount), { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const formatPercent = (value: number) => {
    if (isNaN(value) || !isFinite(value)) return 'TBD';
    return `${value.toFixed(2)}%`;
  };

  /** Right-aligned dollar column with its definition as the header tooltip; negatives in red when flagged. */
  const moneyColumn = (label: string, key: keyof ProjectSummaryRow, redWhenNegative = false) => ({
    label: `${label} ($)`,
    key,
    sortable: true,
    width: '130px',
    headerTooltip: COLUMN_HELP[key],
    render: (row: ProjectSummaryRow) => {
      const amount = Number(row[key]) || 0;
      return (
        <span style={{ textAlign: 'right', display: 'block', color: redWhenNegative && amount < 0 ? 'red' : 'inherit' }}>
          {formatCurrency(amount)}
        </span>
      );
    }
  });

  const formatCurrencyForCSV = (amount: number) => {
    return Math.round(amount).toFixed(0);
  };

  const formatPercentForCSV = (value: number) => {
    if (isNaN(value) || !isFinite(value)) return '';
    return value.toFixed(2);
  };

  const handleRowContextMenu = (row: ProjectSummaryRow, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      projectId: row.projectId
    });
  };

  const handleContextMenuAction = (action: string) => {
    if (!contextMenu) return;
    
    if (action === 'view-project') {
      const projectUrl = `/projects/${contextMenu.projectId}`;
      window.open(projectUrl, '_blank');
    }
    
    setContextMenu(null);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [contextMenu]);

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      // Fetch all data for export (no pagination)
      const response = await projectService.getProjectSummary(summaryFilters(), undefined, { sortField, sortDirection });
      const exportData = response.data;

      if (exportData.length === 0) {
        setErrorMessage('No data to export. Please run the report first.');
        setLoadingExport(false);
        return;
      }

      // Define CSV headers
      const headers = [
        'Project Number',
        'Sage Job Number',
        'Project Name',
        'Budget ($)',
        'Costs Used ($)',
        'Costs In Action ($)',
        'PO Costs ($)',
        'PO Remaining ($)',
        'Est. Remaining Budget ($)',
        'Est. Total Costs ($)',
        'Value ($)',
        'Est. Gross Margin ($)',
        'Est. GM%',
        'Invoiced ($)',
        'Invoices Pending ($)'
      ];

      // Convert data to CSV rows
      const csvRows = exportData.map((row: ProjectSummaryRow) => [
        row.projectNumber || '',
        row.sageJobNumber || '',
        `"${row.projectName.replace(/"/g, '""')}"`, // Escape quotes in project name
        formatCurrencyForCSV(row.budget),
        formatCurrencyForCSV(row.costsUsed),
        formatCurrencyForCSV(row.costsInAction),
        formatCurrencyForCSV(row.poCosts),
        formatCurrencyForCSV(row.poRemaining),
        formatCurrencyForCSV(row.estRemainingBudget),
        formatCurrencyForCSV(row.estTotalCosts),
        formatCurrencyForCSV(row.value),
        formatCurrencyForCSV(row.estGrossMargin),
        formatPercentForCSV(row.estGrossMarginPercent),
        formatCurrencyForCSV(row.invoiced),
        formatCurrencyForCSV(row.invoiceRemaining)
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with current date
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const filename = `QBS_Project_Summary_${year}_${month}_${day}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setErrorMessage('Failed to export CSV. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <FilterField>
              <Form.Group className="tw-mb-1">
                <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.serviceArea}
                  onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                  disabled={loadingServiceAreas}
                >
                  <option value="">All Service Areas</option>
                  {serviceAreas.map((area) => (
                    <option key={area.id} value={area.id.toString()}>
                      {area.name || area.code || 'Unknown'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </FilterField>
            <FilterField>
              <Form.Group className="tw-mb-1">
                <Form.Label className="tw-text-sm tw-mb-1">Project</Form.Label>
                <MultiSelectDropdown<Project>
                  options={projectOptions}
                  value={filters.projectIds}
                  onChange={(value) => setFilters(prev => ({ ...prev, projectIds: value.map(String) }))}
                  getValue={(project) => String(project.id)}
                  formatOption={(project) => (project.projectNumber ? `${project.projectNumber} - ${project.name}` : project.name)}
                  formatDisplay={(project) => project.projectNumber || project.name}
                  placeholder="All Projects"
                  label=""
                  maxDisplayItems={2}
                />
              </Form.Group>
            </FilterField>
            <FilterField>
              <Form.Group className="tw-mb-1">
                <Form.Label className="tw-text-sm tw-mb-1">Fiscal Year</Form.Label>
                <MultiSelectDropdown<string>
                  options={fiscalYearOptions}
                  value={filters.fiscalYear}
                  onChange={(value) => setFilters(prev => ({ ...prev, fiscalYear: value as string[] }))}
                  getValue={(year) => year}
                  formatOption={(year) => year}
                  placeholder="Select Fiscal Years"
                  label=""
                  maxDisplayItems={2}
                />
              </Form.Group>
            </FilterField>
            <FilterField>
              <Form.Group className="tw-mb-1">
                <Form.Label className="tw-text-sm tw-mb-1">Type of Projects</Form.Label>
                <MultiSelectDropdown<ProjectType>
                  options={projectTypeOptions}
                  value={filters.projectTypes}
                  onChange={(value) => setFilters(prev => ({ ...prev, projectTypes: value as string[] }))}
                  getValue={(type) => type}
                  formatOption={(type) => type}
                  placeholder="Select Project Types"
                  label=""
                />
              </Form.Group>
            </FilterField>
            <FilterField>
              <Form.Group className="tw-mb-1">
                <Form.Label className="tw-text-sm tw-mb-1">Project Status</Form.Label>
                <MultiSelectDropdown<{ value: ProjectStatus; label: string }>
                  options={projectStatusOptions}
                  value={filters.projectStatuses}
                  onChange={(value) => setFilters(prev => ({ ...prev, projectStatuses: value as string[] }))}
                  getValue={(status) => status.value}
                  formatOption={(status) => status.label}
                  placeholder="Select Project Statuses"
                  label=""
                />
              </Form.Group>
            </FilterField>
          </FilterGrid>
        </ReportParametersCard>
      </div>
      
      {/* Report Data Section - fills the remaining pane height */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">Project Summary Reporting</h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {projectSummaryData.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={projectSummaryData.length === 0 || loadingExport}
                >
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              )}
            </div>
          </Card.Header>

          {/* Summary Totals Card */}
          {projectSummaryData.length > 0 && (
            <ProjectSummaryTotalsCard totals={summaryTotals} />
          )}

          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
              <ReportPreviewTable<ProjectSummaryRow>
                columns={[
                { 
                  label: 'Project Number', 
                  key: 'projectNumber', 
                  sortable: false, 
                  width: '120px',
                  render: (row: ProjectSummaryRow) => {
                    if (!row.projectNumber) {
                      return <span>-</span>;
                    }
                    return (
                      <a
                        href={`/projects/${row.projectId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(`/projects/${row.projectId}`, '_blank');
                        }}
                        style={{
                          color: '#0066cc',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: 'normal',
                          display: 'inline-block'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#0052a3';
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#0066cc';
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        title={`Click to open project ${row.projectNumber} in new tab`}
                      >
                        {row.projectNumber}
                      </a>
                    );
                  }
                },
                { label: 'Sage Job Number', key: 'sageJobNumber', sortable: true, width: '120px' },
                { label: 'Project Name', key: 'projectName', sortable: true, width: '200px' },
                moneyColumn('Budget', 'budget'),
                moneyColumn('Costs Used', 'costsUsed'),
                moneyColumn('Costs In Action', 'costsInAction'),
                moneyColumn('PO Costs', 'poCosts'),
                moneyColumn('PO Remaining', 'poRemaining'),
                moneyColumn('Est. Remaining Budget', 'estRemainingBudget', true),
                moneyColumn('Est. Total Costs', 'estTotalCosts'),
                moneyColumn('Value', 'value'),
                moneyColumn('Est. Gross Margin', 'estGrossMargin', true),
                { label: 'Est. GM%', key: 'estGrossMarginPercent', sortable: true, width: '100px', headerTooltip: COLUMN_HELP.estGrossMarginPercent, render: (row: ProjectSummaryRow) => {
                    const gmPercent = row.estGrossMarginPercent;
                    let color = 'inherit';
                    if (gmPercent <= 0) {
                      color = 'red';
                    } else if (gmPercent > 0 && gmPercent <= 10) {
                      color = '#ffc107'; // yellow
                    } else if (gmPercent > 10) {
                      color = 'green';
                    }
                    return <span style={{ textAlign: 'right', display: 'block', color }}>{formatPercent(gmPercent)}</span>;
                  }},
                moneyColumn('Invoiced', 'invoiced'),
                moneyColumn('Invoices Pending', 'invoiceRemaining')
              ]}
                data={projectSummaryData}
                pagination={{
                  ...pagination,
                  onPageChange: handlePageChange,
                  onPageSizeChange: handlePageSizeChange
                }}
                loading={loadingPreview}
                emptyMessage="No data available. Click 'Run Report' to generate the report."
                isFiltersCollapsed={isFiltersCollapsed}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                tableHeight="100%"
              />
          </Card.Body>
        </Card>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0"
            onClick={() => setContextMenu(null)}
            style={{ zIndex: 20000 }}
          />
          <div
            ref={contextMenuRef}
            className="tw-fixed tw-bg-surface tw-shadow tw-rounded tw-py-2"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              zIndex: 20001,
              minWidth: '200px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            <button
              className="tw-px-3 tw-py-2 tw-w-full tw-text-left tw-border-0"
              onClick={() => handleContextMenuAction('view-project')}
              style={{
                lineHeight: '1.4',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <i className="bi bi-folder2-open" style={{ flexShrink: 0 }}></i>
              <span>View Project Details</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QBSProjectSummaryView;